/**
 * Перенос `lib/src/config/router/routes/routes.dart` вместе со
 * сгенерированным `routes.g.dart`.
 *
 * В Dart каждый маршрут — класс `GoRouteData`: геттер `location` собирал
 * адрес, а `go`/`push` доставали навигатор из `BuildContext`. В React
 * Router навигатор приходит хуком, поэтому он передаётся первым
 * аргументом. Соответствие вызовов точное:
 *
 *     context.go(location)    -> navigate(location, { replace: true })
 *     context.push(location)  -> navigate(location)
 *
 * С `context.pop()` соответствие не такое прямое. В go_router `go()`
 * пересобирал стек по дереву маршрутов, и под каждым экраном всегда лежал
 * его родитель — для чата, PIN, адреса, аватаров и журнала это
 * `/select-language`. Поэтому `pop()` там означал «на экран языка», а не
 * «назад по истории», и переносится явным переходом. `navigate(-1)`
 * остаётся только там, где экран действительно открыт через `push`, —
 * это страница источников поверх чата.
 *
 *     context.pop()           -> LanguageRoute.go(navigate)   // дети /select-language
 *     context.pop()           -> navigate(-1)                 // ai-message-source
 *
 * Здесь только адреса и переходы; дерево маршрутов собирает `router.tsx` —
 * в Dart за него отвечал сгенерированный `$appRoutes`.
 *
 * Чего не стало: `RootRoute` (пустой `SizedBox()`, нужный лишь как корень
 * генератора — в вебе его роль играет перенаправление с `/`) и
 * `InstalledAppsRoute` вместе с экраном списка установленных приложений —
 * он убран с ролью лаунчера.
 *
 * Отличие в адресе: `_$VerifyAdminTypeEnumMap` писал в путь `change-person`
 * и `exit-app`, а здесь сегментом служит само значение `VerifyAdminType`
 * (`changePerson`, `exitApp`) — ровно то, что читает обратно
 * `verifyAdminTypeFromParam`.
 */

import { useMemo } from 'react';
import { useLocation, type NavigateFunction } from 'react-router';

import type { AppLanguage } from '@/domain/models/appLanguage';
import { VerifyAdminType } from '@/presentation/pages/verifyAdmin/verifyAdminType';
import {
  ChatRole,
  type AssistantMessage,
} from '@/presentation/providers/chatController/chatMessage';

/** `TypedGoRoute<LanguageRoute>(path: '/select-language')` — общий корень. */
const LANGUAGE_LOCATION = '/select-language';

/** `Uri.encodeComponent` из сгенерированного `location`. */
function segment(value: string | number): string {
  return encodeURIComponent(String(value));
}

/**
 * `context.go(location)`.
 *
 * go_router заменял текущую точку стека, а не добавлял новую; ближайшее
 * этому в React Router — переход с `replace: true`.
 */
function goTo(navigate: NavigateFunction, location: string): void {
  navigate(location, { replace: true });
}

/** `context.push(location)` — маршрут кладётся поверх текущего. */
function pushTo(navigate: NavigateFunction, location: string): void {
  navigate(location);
}

/** `class LanguageRoute` — экран выбора языка. */
export const LanguageRoute = {
  location: LANGUAGE_LOCATION,
  go: (navigate: NavigateFunction): void => goTo(navigate, LANGUAGE_LOCATION),
  push: (navigate: NavigateFunction): void => pushTo(navigate, LANGUAGE_LOCATION),
} as const;

/** `class PersonsRoute` — выбор аватара. */
const PERSONS_LOCATION = `${LANGUAGE_LOCATION}/persons`;

export const PersonsRoute = {
  location: PERSONS_LOCATION,
  go: (navigate: NavigateFunction): void => goTo(navigate, PERSONS_LOCATION),
  push: (navigate: NavigateFunction): void => pushTo(navigate, PERSONS_LOCATION),
} as const;

/**
 * Пароль планшета — вход, пока в браузере нет сессии. Вне дерева
 * `/select-language`: у экрана нет родителя, «назад» с него некуда.
 */
const TABLET_LOGIN_LOCATION = '/tablet-login';

export const TabletLoginRoute = {
  location: TABLET_LOGIN_LOCATION,
  go: (navigate: NavigateFunction): void => goTo(navigate, TABLET_LOGIN_LOCATION),
} as const;

/** `class DiagnosticsRoute` — журнал для разработчика. */
const DIAGNOSTICS_LOCATION = `${LANGUAGE_LOCATION}/diagnostics`;

export const DiagnosticsRoute = {
  location: DIAGNOSTICS_LOCATION,
  go: (navigate: NavigateFunction): void => goTo(navigate, DIAGNOSTICS_LOCATION),
  push: (navigate: NavigateFunction): void => pushTo(navigate, DIAGNOSTICS_LOCATION),
} as const;

/**
 * `BaseUrlRoute({this.verifyAdminType = VerifyAdminType.changePerson})` —
 * умолчание то же, что в Dart-конструкторе.
 */
function baseUrlLocation(
  verifyAdminType: VerifyAdminType = VerifyAdminType.changePerson,
): string {
  return `${LANGUAGE_LOCATION}/${segment(verifyAdminType)}/baseUrl`;
}

export const BaseUrlRoute = {
  location: baseUrlLocation,
  go: (navigate: NavigateFunction, verifyAdminType?: VerifyAdminType): void => {
    goTo(navigate, baseUrlLocation(verifyAdminType));
  },
} as const;

/** `VerifyAdminRoute({required this.type})` — проверка PIN. */
function verifyAdminLocation(type: VerifyAdminType): string {
  return `${LANGUAGE_LOCATION}/${segment(type)}/verify-admin`;
}

export const VerifyAdminRoute = {
  location: verifyAdminLocation,
  go: (navigate: NavigateFunction, type: VerifyAdminType): void => {
    goTo(navigate, verifyAdminLocation(type));
  },
} as const;

/**
 * `ChatRoute({required this.lang, required this.personId})`.
 *
 * В Dart `lang` объявлен как `String`, а страница разбирала его через
 * `AppLanguage.fromString`. Здесь тип сразу [AppLanguage] — значением
 * перечисления служит тот же код языка, так что адрес не изменился.
 */
export interface ChatRouteArgs {
  readonly lang: AppLanguage;
  readonly personId: number;
}

function chatLocation({ lang, personId }: ChatRouteArgs): string {
  return `${LANGUAGE_LOCATION}/${segment(lang)}/chat/${segment(personId)}`;
}

export const ChatRoute = {
  location: chatLocation,
  go: (navigate: NavigateFunction, args: ChatRouteArgs): void => {
    goTo(navigate, chatLocation(args));
  },
} as const;

/**
 * Что `AiMessageSourceRoute` везла помимо адреса: `$extra` (сообщение) и
 * `hero` (тег перелёта).
 *
 * В go_router `hero` ехал в query, а сообщение — в `extra`. Здесь оба
 * лежат в состоянии истории: адрес остаётся чистым, и `ChatView` сравнивает
 * с ним `pathname`, чтобы понять, открыт ли оверлей.
 */
export interface AiMessageSourceExtra {
  readonly hero: string | null;
  readonly message: AssistantMessage;
}

export interface AiMessageSourcePushArgs extends ChatRouteArgs {
  readonly hero?: string | null;
  readonly message: AssistantMessage;
}

function aiMessageSourceLocation(args: ChatRouteArgs): string {
  return `${chatLocation(args)}/ai-message-source`;
}

function pushAiMessageSource(
  navigate: NavigateFunction,
  args: AiMessageSourcePushArgs,
): void {
  const { lang, personId, hero = null, message } = args;
  const extra: AiMessageSourceExtra = { hero, message };
  navigate(aiMessageSourceLocation({ lang, personId }), { state: extra });
}

/**
 * Разбор состояния истории.
 *
 * В Dart `state.extra as AssistantMessage` не проверялся: `extra` мог
 * появиться только вместе с переходом. В браузере состояние приходит
 * извне — адрес открывают напрямую, историю восстанавливают после
 * перезагрузки, — поэтому данные с границы проверяются, и на чужой формат
 * страница получает `null` (она сама уходит назад).
 */
function isAssistantMessage(value: unknown): value is AssistantMessage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.role !== ChatRole.assistant) return false;
  if (typeof candidate.id !== 'number') return false;
  if (typeof candidate.message !== 'string') return false;
  return Array.isArray(candidate.chunks);
}

function parseAiMessageSourceExtra(state: unknown): AiMessageSourceExtra | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = state as Record<string, unknown>;
  const message = candidate.message;
  if (!isAssistantMessage(message)) return null;
  // Тег перелёта необязателен и в Dart: `hero` был `String?`, а его
  // отсутствие означало показ без анимации.
  const hero = candidate.hero;
  return { hero: typeof hero === 'string' ? hero : null, message };
}

/**
 * `_fromState` из `routes.g.dart` — то, с чем страница источника строится.
 *
 * Результат мемоизируется по состоянию истории: у `AiMessageSourcePage`
 * есть эффект с `args` в зависимостях, и новая ссылка на каждый рендер
 * запускала бы его вхолостую.
 */
function useAiMessageSourceArgs(): AiMessageSourceExtra | null {
  const { state } = useLocation();
  return useMemo(() => parseAiMessageSourceExtra(state), [state]);
}

export const AiMessageSourceRoute = {
  location: aiMessageSourceLocation,
  push: pushAiMessageSource,
  useArgs: useAiMessageSourceArgs,
} as const;
